// Cache Helpers
//
// This module manages caching for label positioning decisions across Chart.js render cycles.
// Chart.js calls label positioning callbacks multiple times during rendering, animations, and updates.
// This cache ensures calculations are shared between the `align` and `offset` callbacks while
// automatically clearing stale data when a new render cycle begins.

import type { CollisionCache, CachedDecision, ChartArea, PositioningOption } from './types'

// Create a new collision cache with render cycle tracking
export const createCollisionCache = (): CollisionCache => ({
  data: new Map(),
  renderCycleId: null,
  maxIndexProcessed: -1
})

// Generate unique ID for current render cycle based on chart dimensions
const generateRenderCycleId = (chartArea: ChartArea | null): string | null => {
  if (!chartArea) return null
  return `${chartArea.left}-${chartArea.right}-${chartArea.width}`
}

// Detect if a new render cycle has started
const isNewRenderCycle = ({
  currentIndex,
  cache,
  currentRenderCycleId
}: {
  currentIndex: number
  cache: CollisionCache
  currentRenderCycleId: string | null
}): boolean => {
  // Wrapped around: processing index 0 after higher indices
  const hasWrappedAround = currentIndex === 0 && cache.maxIndexProcessed > 0

  // Chart dimensions changed (resize or re-render)
  const dimensionsChanged = currentRenderCycleId !== null && currentRenderCycleId !== cache.renderCycleId

  return hasWrappedAround || dimensionsChanged
}

// Manage cache lifecycle - clear stale data and track render cycles
export const manageCacheLifecycle = ({
  ctx,
  cache,
  chartArea
}: {
  ctx: { dataIndex: number }
  cache: CollisionCache
  chartArea: ChartArea
}): void => {
  const currentRenderCycleId = generateRenderCycleId(chartArea)

  if (isNewRenderCycle({ currentIndex: ctx.dataIndex, cache, currentRenderCycleId })) {
    // New render cycle detected - clear stale data
    cache.data.clear()
    cache.maxIndexProcessed = -1
    cache.renderCycleId = currentRenderCycleId
  } else if (currentRenderCycleId && !cache.renderCycleId) {
    // First render - initialize render cycle ID
    cache.renderCycleId = currentRenderCycleId
  }

  // Track max index to detect wrap-around
  cache.maxIndexProcessed = Math.max(cache.maxIndexProcessed, ctx.dataIndex)
}

// Store positioning decision in cache for offset callback
export const cachePositioningDecision = ({
  cache,
  dataIndex,
  chosenOption,
  baseOffset,
  alignment
}: {
  cache: CollisionCache
  dataIndex: number
  chosenOption: PositioningOption | null
  baseOffset: number
  alignment: 'left' | 'right' | 'center' | 'top'
}): void => {
  cache.data.set(dataIndex, {
    option: chosenOption,
    baseOffset,
    alignment
  })
}

// Retrieve cached positioning decision
export const getCachedDecision = (cache: CollisionCache, dataIndex: number): CachedDecision | undefined => {
  return cache.data.get(dataIndex)
}
